import { Link, useSearchParams } from "react-router-dom";
import { CtaBand } from "../../../components/site/CtaBand";
import { PageHead } from "../../../components/site/PageHead";
import { ProductCard } from "../../../components/site/ProductCard";
import { PRODUCTS, type Product } from "../../../content/products";
import { IMAGES, RANGES, type RangeId } from "../../../content/site";

/** The range filter's value: one of the three ranges, or "all" (the default). */
export type RangeFilter = RangeId | "all";

/** BR-WEB-011 validation — an unknown or missing ?range= value falls back to All. */
export function parseRange(value: string | null): RangeFilter {
  return RANGES.some((r) => r.id === value) ? (value as RangeId) : "all";
}

interface ProductsPageProps {
  /** The product list. Defaults to the bundled content; tests pass a reduced list for the Empty state. */
  products?: readonly Product[];
}

/**
 * Products — replaces legacy /products, which was a banner and nothing else (Story #25440).
 *
 * BR-WEB-010: every product grouped under its range heading, All by default.
 * BR-WEB-011: the range chips filter client-side and write ?range={slug}; each chip shows its count.
 * BR-WEB-012: each tile shows the product name, its tagline and hero photo, and links to /products/{slug}.
 * Static content only — no network calls beyond the bundled images.
 */
export default function ProductsPage({ products = PRODUCTS }: ProductsPageProps) {
  const [params, setParams] = useSearchParams();
  const selected = parseRange(params.get("range"));
  const inRange = (id: RangeId) => products.filter((p) => p.range === id);
  const groups = RANGES.filter((r) => selected === "all" || r.id === selected)
    .map((r) => ({ range: r, items: inRange(r.id) }))
    .filter((g) => g.items.length > 0);

  const choose = (value: RangeFilter) => {
    const next = new URLSearchParams(params);
    if (value === "all") next.delete("range");
    else next.set("range", value);
    setParams(next, { preventScrollReset: true });
  };

  const chips: { value: RangeFilter; label: string; count: number }[] = [
    { value: "all", label: "All", count: products.length },
    ...RANGES.map((r) => ({ value: r.id, label: r.name, count: inRange(r.id).length })),
  ];

  return (
    <>
      <PageHead
        crumbs={[{ label: "Products" }]}
        title="Our products"
        lead="With Toowoomba’s only outdoor display centre, you’re sure to find the perfect solution for your home — every range supplied and installed by our own team."
        bg={IMAGES.bannerProducts}
      />
      <section className="gw-sec" style={{ paddingTop: 48 }} aria-label="Products by range">
        <div className="gw-wrap">
          <div className="gw-chips" role="group" aria-label="Filter by range">
            {chips.map((c) => (
              <button
                key={c.value}
                type="button"
                className="gw-chip"
                aria-pressed={selected === c.value}
                data-range={c.value}
                onClick={() => choose(c.value)}
              >
                {c.label}
                <span>{c.count}</span>
              </button>
            ))}
          </div>

          {groups.length === 0 ? (
            <p className="gw-placeholder" style={{ marginTop: 40 }} role="status" data-testid="products-empty">
              Nothing in this range right now — <Link to="/products">see all products</Link>.
            </p>
          ) : (
            groups.map(({ range, items }) => (
              <section key={range.id} aria-labelledby={`range-${range.id}`} data-testid={`range-${range.id}`}>
                <div className="gw-grouph">
                  <h2 id={`range-${range.id}`}>{range.name}</h2>
                  <span>
                    {items.length} {items.length === 1 ? "product" : "products"}
                  </span>
                </div>
                <div className="gw-grid">
                  {items.map((p) => (
                    <ProductCard key={p.slug} product={p} variant="tile" />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      </section>
      <CtaBand />
    </>
  );
}
